import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { getRelativeDateLabel, hasValue, isRelativeDate, toDate, dateToISOFormat } from '@utils';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import Calendar from './Calendar/Calendar';
import classNames from 'classnames';
import { isValid, isAfter, format } from 'date-fns';
import { _getDate } from './utils';
import { UXPContext } from "@core/context/Context";
import { Dropdown } from '@widget/popups/Dropdown';
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import Input from '../Input';
import FormField from '../FormField';
import Label from '../Label';
import ButtonComponent from "@widget/buttons/ButtonComponent";
/**
 *
 * @export
 *
 * This component is used to select a date range.
 *
 * @example
 * ```
 *  <DateRangePicker
 *      startDate={startDate}
 *      endDate={endDate}
 *      closeOnSelect
 *      onChange={(newStart, newEnd) => { setStartDate(newStart); setEndDate(newEnd) }}
 *  />
 * ```
 *
 */
const DateRangePicker = (props) => {
    const context = React.useContext(UXPContext);
    // props
    let { title = context.$L('uxp-core.text.select-a-date-range'), startDate, endDate, onChange, closeOnSelect = false, options, disableInput, hideInput, hideLabels, showFullMonthName = true, preset, presets, dropdownClassname, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, onClear } = props;
    const defaultPresets = [
        { label: 'Past 7 days', startDate: '-7d', endDate: '0d' },
        { label: 'Past 30 days', startDate: '-30d', endDate: '0d' },
        { label: 'Past 365 days', startDate: '-365d', endDate: '0d' },
    ];
    const availablePresets = ((presets === null || presets === void 0 ? void 0 : presets.customPresets) || defaultPresets || []);
    availablePresets.push({ label: 'Custom', startDate: '', endDate: '' });
    // states
    const [showPicker, setShowPicker] = React.useState(false);
    const [startValue, setStartValue] = React.useState(startDate);
    const [endValue, setEndValue] = React.useState(endDate);
    const [showInput, setShowInput] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const [showCalendar, setShowCalendar] = React.useState(true);
    const [isCustom, setIsCustom] = React.useState(false);
    const triggerRef = React.useRef(null);
    const presetsContainerRef = React.useRef(null);
    React.useEffect(() => {
        setStartValue(startDate);
        setEndValue(endDate);
    }, [startDate, endDate]);
    React.useEffect(() => {
        // Show calendar when:
        // 1. No presets enabled
        // 2. Preset is 'Custom'
        // 3. parseRelativeDatesOnSelect is true (user wants to see actual dates)
        const shouldShowCalendar = !(presets === null || presets === void 0 ? void 0 : presets.enable) ||
            !hasValue(preset) ||
            (preset === null || preset === void 0 ? void 0 : preset.toLowerCase()) === 'custom' ||
            (presets === null || presets === void 0 ? void 0 : presets.parseRelativeDatesOnSelect) === true;
        setShowCalendar(shouldShowCalendar);
    }, [preset, presets]);
    // Reset isCustom flag when dropdown opens if dates match a preset
    React.useEffect(() => {
        if (showPicker && (presets === null || presets === void 0 ? void 0 : presets.enable) && !isCustom) {
            const matchingPreset = getMatchingPreset();
            if (matchingPreset && matchingPreset.toLowerCase() !== 'custom') {
                setIsCustom(false);
            }
        }
    }, [showPicker]);
    // Auto-scroll active preset to center
    React.useEffect(() => {
        if (showPicker && presetsContainerRef.current && (presets === null || presets === void 0 ? void 0 : presets.enable)) {
            setTimeout(() => {
                var _a;
                const activeButton = (_a = presetsContainerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.uxpcore_daterangepicker__preset-btn.active');
                if (activeButton) {
                    activeButton.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                }
            }, 150);
        }
    }, [showPicker, isCustom, preset]);
    const closePicker = () => {
        const hasStart = hasValue(startDate) && isValid(_getDate(startDate));
        const hasEnd = hasValue(endDate) && isValid(_getDate(endDate));
        // If both dates exist, check if we need to swap (start > end)
        if (hasStart && hasEnd) {
            const _sd = _getDate(startDate);
            const _ed = _getDate(endDate);
            if (_sd && _ed && !isAfter(_ed, _sd)) {
                // Swap: start is after end (or equal)
                onChange(_ed, _sd);
                // Delay closing to allow state to update
                setTimeout(() => {
                    setShowPicker(false);
                    setShowInput(false);
                    setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
                }, 0);
                return; // Exit early
            }
            // Both dates valid, continue to close
        }
        else if (hasStart !== hasEnd) {
            // Only one date is selected (XOR), clear both to prevent invalid state
            onChange(null, null);
        }
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    const onChangeHandler = (range) => {
        onChange(range.start, range.end);
        if (range.start && range.end && closeOnSelect) {
            setShowPicker(false);
        }
    };
    const handleInputBlur = (val, isStart) => {
        let _date = new Date(val);
        if (!isValid(_date))
            _date = null;
        let _sd = isStart ? _date : _getDate(startDate);
        let _ed = isStart ? _getDate(endDate) : _date;
        if (!isValid(_sd))
            _sd = null;
        if (!isValid(_ed))
            _ed = null;
        // Allow partial input and free selection - don't swap here
        onChange(_sd, _ed);
        // Don't close on blur - user can click Done button or click outside to close
    };
    function parseDate(val, _format, _parseRelativeDate) {
        if (typeof val === "string" && isRelativeDate(val) && !((presets === null || presets === void 0 ? void 0 : presets.parseRelativeDatesOnSelect) || _parseRelativeDate)) {
            return _format ? getRelativeDateLabel(val) : val;
        }
        const parsed = toDate(val);
        return (_format && parsed) ? format(parsed, "yyyy-MM-dd") : parsed;
    }
    // Check if current date range matches any preset
    function getMatchingPreset() {
        if (!(presets === null || presets === void 0 ? void 0 : presets.enable) || !startDate || !endDate)
            return null;
        const currentStart = parseDate(startDate, false, true);
        const currentEnd = parseDate(endDate, false, true);
        if (!currentStart || !currentEnd)
            return null;
        for (const preset of availablePresets) {
            if (preset.label.toLowerCase() === 'custom')
                continue;
            const presetStart = parseDate(preset.startDate, false, true);
            const presetEnd = parseDate(preset.endDate, false, true);
            if (!presetStart || !presetEnd)
                continue;
            // Compare dates (same day)
            const startMatches = format(currentStart, 'yyyy-MM-dd') === format(presetStart, 'yyyy-MM-dd');
            const endMatches = format(currentEnd, 'yyyy-MM-dd') === format(presetEnd, 'yyyy-MM-dd');
            if (startMatches && endMatches) {
                return preset.label;
            }
        }
        return null;
    }
    const handlePresetSelect = (selectedPreset) => {
        if ((selectedPreset === null || selectedPreset === void 0 ? void 0 : selectedPreset.toLowerCase()) == 'custom') {
            setShowCalendar(true);
            setIsCustom(true);
            // Clear preset and keep current dates
            onChange(startDate, endDate, 'Custom');
            return;
        }
        const _preset = availablePresets.find(pr => { var _a; return (((_a = pr.label) === null || _a === void 0 ? void 0 : _a.toLowerCase()) == (selectedPreset === null || selectedPreset === void 0 ? void 0 : selectedPreset.toLowerCase())); });
        if (!_preset)
            return;
        // User selected a preset (not Custom), clear isCustom flag
        setIsCustom(false);
        // Always parse relative dates to actual dates for internal use
        // If parseRelativeDatesOnSelect is true, return actual dates
        // If false, return relative date strings but ensure Calendar receives parsed dates
        const shouldParseForCallback = presets === null || presets === void 0 ? void 0 : presets.parseRelativeDatesOnSelect;
        const sd = shouldParseForCallback ? parseDate(_preset.startDate) : _preset.startDate;
        const ed = shouldParseForCallback ? parseDate(_preset.endDate) : _preset.endDate;
        onChange(sd, ed, selectedPreset);
    };
    const pillStyles = {};
    if (!!(props === null || props === void 0 ? void 0 : props.renderAsPill)) {
        pillStyles.width = 'auto';
        pillStyles.display = 'inline-block';
        const { minWidth, maxWidth } = props.renderAsPill;
        if (hasValue(minWidth))
            pillStyles.minWidth = minWidth + 'px';
        if (hasValue(maxWidth))
            pillStyles.maxWidth = maxWidth + 'px';
    }
    function renderPresets() {
        if (!(presets === null || presets === void 0 ? void 0 : presets.enable))
            return null;
        const matchingPreset = getMatchingPreset();
        return (_jsx("div", { className: "uxpcore_daterangepicker__presets", ref: presetsContainerRef, children: (presets === null || presets === void 0 ? void 0 : presets.renderPreset)
                ? (availablePresets || []).map((pr, i) => {
                    return presets.renderPreset(pr, i, (selected) => handlePresetSelect(selected));
                })
                : (availablePresets).map((pr, i) => {
                    // Determine if this preset button should be active:
                    // Priority: isCustom flag > preset prop > calculated matching
                    let isActive;
                    if (isCustom) {
                        // When isCustom flag is true, only Custom button is active
                        isActive = pr.label.toLowerCase() === 'custom';
                    }
                    else if (pr.label.toLowerCase() === 'custom') {
                        // Custom is active if explicitly selected OR if no preset matches
                        isActive = (preset === null || preset === void 0 ? void 0 : preset.toLowerCase()) === 'custom' || (!preset && !matchingPreset);
                    }
                    else {
                        // For other presets: Active if dates match this preset (either by prop or calculated)
                        isActive = preset === pr.label || matchingPreset === pr.label;
                    }
                    return (_jsx("button", { className: classNames("uxpcore_daterangepicker__preset-btn", { "active": isActive }), onClick: () => handlePresetSelect(pr.label), children: pr.label }, i));
                }) }));
    }
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_daterangepicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_daterangepicker__header", children: [_jsx("div", { className: "uxpcore_daterangepicker__title", children: showInput ? context.$L('uxp-core.text.enter-date') : title }), !hideInput && (_jsx("div", { className: "uxpcore_daterangepicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-calendar') : context.$L('uxp-core.text.type-date'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'calendar' : 'keyboard'] }) }))] }), renderPresets(), showInput && !hideInput && (_jsxs("div", { className: "uxpcore_daterangepicker__input-container", children: [_jsxs(FormField, { noMargin: true, noPadding: true, children: [_jsx(Label, { children: context.$L('uxp-core.text.from') }), _jsx(Input, { type: "text", value: typeof startValue === "string" ? startValue : dateToISOFormat(startValue), onChange: (val) => setStartValue(val), onBlur: (val) => handleInputBlur(val, true), readOnly: disableInput, placeholder: context.$L('uxp-core.text.enter-date'), fullWidth: true, hideClear: true })] }), _jsxs(FormField, { noMargin: true, noPadding: true, children: [_jsx(Label, { children: context.$L('uxp-core.text.to') }), _jsx(Input, { type: "text", value: typeof endValue === "string" ? endValue : dateToISOFormat(endValue), onChange: (val) => setEndValue(val), onBlur: (val) => handleInputBlur(val, false), readOnly: disableInput, placeholder: context.$L('uxp-core.text.enter-date'), fullWidth: true, hideClear: true })] })] })), !showInput && showCalendar && (_jsx(Calendar, Object.assign({ range: {
                    start: parseDate(startDate, false, true),
                    end: parseDate(endDate, false, true)
                }, onSelect: (range) => onChangeHandler(range) }, options, { dateRange: true, size: "small", showFullMonthName: showFullMonthName }))), (!closeOnSelect || showInput) && (_jsx("div", { className: "uxpcore_daterangepicker__footer", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.done'), onClick: closePicker, size: "small" }) }))] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = (!!(startDate || endDate) && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(null, null); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    // Format the date range as a single value, or show placeholder if dates are null
    const formattedRange = React.useMemo(() => {
        // If user explicitly clicked Custom, always show actual dates
        if (isCustom) {
            const start = parseDate(startDate, true, true);
            const end = parseDate(endDate, true, true);
            if (!start && !end) {
                return context.$L('uxp-core.text.select-a-date-range');
            }
            if (!start) {
                return `${context.$L('uxp-core.text.select-a-date-range')} - ${end}`;
            }
            if (!end) {
                return `${start} - ${context.$L('uxp-core.text.select-a-date')}`;
            }
            return `${start} - ${end}`;
        }
        // Check if dates match any preset (either explicitly set or auto-detected)
        const matchingPreset = getMatchingPreset();
        const activePreset = preset || matchingPreset;
        // If a preset is active (not Custom) AND parseRelativeDatesOnSelect is false, show the preset label
        // If parseRelativeDatesOnSelect is true, show the actual parsed dates instead
        if (activePreset && activePreset.toLowerCase() !== 'custom' && !(presets === null || presets === void 0 ? void 0 : presets.parseRelativeDatesOnSelect)) {
            return activePreset;
        }
        // Otherwise show the date range
        // Always parse to actual dates for display (format: true)
        const start = parseDate(startDate, true, true);
        const end = parseDate(endDate, true, true);
        if (!start && !end) {
            return context.$L('uxp-core.text.select-a-date-range');
        }
        if (!start) {
            return `${context.$L('uxp-core.text.select-a-date-range')} - ${end}`;
        }
        if (!end) {
            return `${start} - ${context.$L('uxp-core.text.select-a-date')}`;
        }
        return `${start} - ${end}`;
    }, [startDate, endDate, preset, presets, context, isCustom]);
    return (_jsxs("div", { className: classNames("uxpcore_daterangepicker", { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--daterangepicker", stateClass, {
                    "uxpcore_input--has-prefix": !hideLabels,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": !!(startDate && endDate),
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [!hideLabels && (_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'calendar'] }) })), _jsx("div", { className: "uxpcore_input__value", children: formattedRange }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_daterangepicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: '20rem', minWidth: "20rem" })] }));
};
export default DateRangePicker;
